import { DocxTemplate, Upload } from '@range.io/basic-types'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import {
    Box,
    Button,
    Flex,
    FlexColumn,
    FlexRow,
    Icon,
    IconButtonWithTooltip,
    Text,
} from '../components-reusable/index.js'
import {
    deleteFromStorage,
    downloadUrlForPath,
    listDocxTemplates,
    uploadToStorage,
} from '../firebase/firebase-facade.js'
import { styled } from '../range-theme/index.js'
import { ReduxActions, ReduxSelectors } from '../redux/index.js'
import AdminNavigationPanel, { AdminNavigationType } from './AdminNavigationPanel.js'
import AdminOrganizationsPanel from './AdminOrganizationsPanel.js'
import { selectFile } from './CollaborationWindow.js'

const StyledContainer = styled(FlexColumn, {
    gap: '32px',
    height: '100%',
    overflow: 'auto',
})

const StyledHeader = styled(FlexRow, {
    justifyContent: 'space-between',
    alignItems: 'center',
})

const StyledSection = styled(FlexColumn, {
    background: '$neutral09',
    br: '6px',
    border: '1px solid $neutral07',
    overflow: 'hidden',
})

const StyledTemplateRow = styled(FlexRow, {
    gap: '12px',
    padding: '0px 8px 0px 16px',
    height: '56px',
    alignItems: 'center',
    background: '$neutral08',
    borderBottom: '1px solid $neutral07',
    color: '$neutral04',

    '&:last-child': {
        borderBottom: 'none',
    },
})

const StyledSectionHeader = styled(FlexRow, {
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0px 8px 0px 16px',
    height: '56px',
    borderBottom: '1px solid $neutral07',
    fontWeight: '400',
    '& > *': {
        color: '$neutral05',
    },
    '&:last-child': {
        borderBottom: 'none',
    },
})

const StyledUploadButton = styled(Button, {
    gap: '8px',
})

/**
 * Reusable section header component
 */
const ReportSectionHeader = ({ title, showUploadButton, onUpload }) => (
    <StyledSectionHeader>
        <Text css={{ fs: '16px' }}>{title}</Text>
        {showUploadButton && (
            <StyledUploadButton variant="primary" size="lg" onClick={onUpload}>
                <Icon name="uploadAsset" iconSize="18px" />
                <Text>Upload Template</Text>
            </StyledUploadButton>
        )}
    </StyledSectionHeader>
)

/**
 * Template section component for both default and custom templates
 * @sig ReportSection :: {String, [String], Boolean, Boolean} -> ReactElement
 */
const ReportSection = ({ title, templates, forCustomTemplates, onDownload, onUpload, onDelete, onReplace }) => {
    const customTemplateRow = templateName => (
        <StyledTemplateRow key={templateName}>
            <FlexColumn css={{ flex: 1 }}>
                <Text>{templateName}</Text>
            </FlexColumn>
            <FlexRow css={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <IconButtonWithTooltip
                    iconName="reset"
                    size="16px"
                    side="bottom"
                    tooltipText="Replace template"
                    variant="neutralHover32"
                    onClick={() => onReplace(templateName)}
                />
                <IconButtonWithTooltip
                    iconName="download"
                    size="16px"
                    side="bottom"
                    tooltipText="Download template"
                    variant="neutralHover32"
                    onClick={() => onDownload(templateName)}
                />
                <IconButtonWithTooltip
                    iconName="trash"
                    size="16px"
                    side="bottom"
                    tooltipText="Delete template"
                    variant="neutralHover32"
                    css={{ color: '$red03', '&:hover': { color: '$red03', backgroundColor: '$red01' } }}
                    onClick={() => onDelete(templateName)}
                />
            </FlexRow>
        </StyledTemplateRow>
    )

    const defaultTemplateRow = templateName => (
        <StyledTemplateRow key={templateName}>
            <FlexColumn css={{ flex: 1 }}>
                <Text>{templateName}</Text>
            </FlexColumn>
            <IconButtonWithTooltip
                iconName="download"
                size="16px"
                side="bottom"
                tooltipText="Download template"
                variant="neutralHover32"
                onClick={() => onDownload(templateName)}
            />
        </StyledTemplateRow>
    )

    const templateCount = templates.length
    const titleWithCount = `${title} (${templateCount})`

    return (
        <StyledSection>
            <ReportSectionHeader title={titleWithCount} showUploadButton={forCustomTemplates} onUpload={onUpload} />
            {templates.map(forCustomTemplates ? customTemplateRow : defaultTemplateRow)}
        </StyledSection>
    )
}

/**
 * Empty state for custom templates section
 * @sig EmptyCustomTemplatesSection :: () -> ReactElement
 */
const EmptyCustomTemplatesSection = ({ onUpload }) => (
    <StyledSection>
        <ReportSectionHeader title="Custom Report Templates" showUploadButton={true} onUpload={onUpload} />
        <FlexColumn css={{ alignItems: 'center', py: '32px', gap: '8px' }}>
            <Text css={{ color: '$neutral05', textAlign: 'center', fs: '18px' }}>0 custom report templates</Text>
            <Text css={{ color: '$neutral05', textAlign: 'center', fs: '14px' }}>
                Get started by downloading one of the default Range report templates, modifying it, then re-uploading it
                here.
            </Text>
        </FlexColumn>
    </StyledSection>
)

const reportError = dispatch => {
    const description =
        "This doesn't usually happen, but we were unable to produce your report. We apologise for the inconvenience."
    const title = 'Oops... something went wrong'
    dispatch(ReduxActions.globalModalDataSet({ title, description, cancelButton: { label: 'Ok' } }))
}

/**
 * Main Reports view component
 * @sig AdminViewReports :: {[String], [String]} -> ReactElement
 */
const AdminViewReports = ({ _listTemplates = listDocxTemplates }) => {
    const onDownload = isCustomTemplate => async templateName => {
        // prettier-ignore
        const getStoragePath = () =>
            isCustomTemplate
                ? `organizations/${organization.id}/docx-templates/${templateName}`
                :                                  `docx-templates/${templateName}`

        try {
            const remoteUrl = await downloadUrlForPath(getStoragePath())
            const blob = await (await fetch(remoteUrl)).blob() // pull bytes
            const localURL = URL.createObjectURL(blob) // strip headers

            const link = document.createElement('a')
            link.href = localURL
            link.download = templateName
            document.body.appendChild(link)
            link.click()
            link.remove()

            URL.revokeObjectURL(localURL) // tidy up
        } catch (e) {
            reportError(dispatch)
        }
    }

    const onUpload = async () => {
        try {
            const files = await selectFile({ mimeTypes: Upload.mimeTypes.docx, multiple: true })
            for (const f of files) await uploadToStorage(`organizations/${organization.id}/docx-templates/${f.name}`, f)
            navigate(0)
        } catch (e) {
            reportError(dispatch)
        }
    }

    const onReplace = async templateName => {
        try {
            const files = await selectFile({ mimeTypes: Upload.mimeTypes.docx, multiple: false })

            for (const f of files)
                await uploadToStorage(`organizations/${organization.id}/docx-templates/${templateName}`, f)
            navigate(0)
        } catch (e) {
            reportError(dispatch)
        }
    }

    const onDelete = async templateName => {
        try {
            await deleteFromStorage(`organizations/${organization.id}/docx-templates/${templateName}`)
            navigate(0)
        } catch (e) {
            reportError(dispatch)
        }
    }

    const navigate = useNavigate()
    const dispatch = useDispatch()
    const organizations = useSelector(ReduxSelectors.organizationsAsArray)
    const organization = useSelector(ReduxSelectors.selectedOrganization)

    const [docxTemplates, setDocxTemplates] = useState([])

    useEffect(() => {
        const fetchTemplates = async () => {
            const docxTemplates = await _listTemplates(organization.id)
            setDocxTemplates(docxTemplates)
        }
        fetchTemplates()
    }, [])

    const defaultReportTemplates = DocxTemplate.defaultTemplateNames(docxTemplates)
    const customReportTemplates = DocxTemplate.customTemplateNames(docxTemplates)

    const customReportsSection =
        customReportTemplates.length > 0 ? (
            <ReportSection
                title="Custom Report Templates"
                templates={customReportTemplates}
                forCustomTemplates={true}
                onDownload={onDownload(true)}
                onUpload={onUpload}
                onDelete={onDelete}
                onReplace={onReplace}
            />
        ) : (
            <EmptyCustomTemplatesSection onUpload={onUpload} />
        )

    return (
        <Flex css={{ w: '100vw', h: '100vh', bg: '$neutral10' }}>
            <AdminOrganizationsPanel
                relativePath="reports"
                organizations={organizations}
                selectedOrganizationId={organization?.id}
            />
            <AdminNavigationPanel selectedPanel={AdminNavigationType.REPORTS} />
            <Box css={{ flexGrow: 3, overflow: 'hidden' }}>
                <Flex direction="column" css={{ height: '100%', p: '64px 42px 42px 42px', gap: 24 }}>
                    <Text css={{ fs: '32px', fw: '700', fg: '$neutral04', lh: '40px' }}>Reports</Text>
                    <StyledContainer>
                        <StyledHeader>
                            <Text css={{ fs: '16px', color: '$neutral05' }}>
                                Create custom reports in Range by exporting your report data to a Microsoft Word (.docx)
                                file. To create a custom report for your organization, download an existing Range report
                                and modify the styles, colors, content, footers, and more. Then, upload your file and
                                use it as a custom report template.
                            </Text>
                        </StyledHeader>

                        <ReportSection
                            title="Default Microsoft Word (.docx) Report Templates"
                            templates={defaultReportTemplates}
                            forCustomTemplates={false}
                            onDownload={onDownload(false)}
                        />

                        {customReportsSection}
                    </StyledContainer>
                </Flex>
            </Box>
        </Flex>
    )
}

export default AdminViewReports
